import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { environment } from '@env/environment';
import { USE_ADMIN_AUTH } from '@core/services/api.service';
import { AuthState } from '@state/auth/auth.state';

/**
 * Adds HTTP Basic auth to every outbound request to the SaltwaterUp API,
 * selecting admin vs. user credentials based on the USE_ADMIN_AUTH context
 * token.
 *
 * Direct port of SaltwaterUpRestClient.m's split between
 * `getRestUserPasswordForAdmin` (used for /users.json) and
 * `getRestUserPasswordForLoggedInUser` (used for everything else).
 *
 * The password for authenticated user calls is the constant "x" — the
 * server treats the api_id itself as the secret. We preserve this
 * behavior to keep wire compatibility with the existing API.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Only intercept requests to our API. Any other absolute URLs
  // (for example, future calls to tile servers or NOAA directly)
  // must not have our Basic creds attached.
  if (!req.url.startsWith(environment.restUrlBase)) {
    return next(req);
  }

  const useAdmin = req.context.get(USE_ADMIN_AUTH);
  let username: string;
  let password: string;

  if (useAdmin) {
    username = environment.adminApiId;
    password = environment.adminPassword;
  } else {
    const store = inject(Store);
    const apiId = store.selectSnapshot(AuthState.apiId);
    if (!apiId) {
      // No authenticated user context available; send the request
      // without an Authorization header and let the server respond
      // with a 401 (or equivalent). Callers should normally already
      // be gated by AuthGuard.
      return next(req);
    }
    username = apiId;
    password = environment.anyUserPassword;
  }

  const credentials = btoa(`${username}:${password}`);
  const authed = req.clone({
    setHeaders: { Authorization: `Basic ${credentials}` },
  });

  return next(authed);
};
